import React from 'react'
import { isEmpty } from 'lodash'
import { useAPI } from 'contexts/APIProvider'
import { Button, Chip, Typography, Alert, Box, MenuItem } from '@mui/material'
import { Delete } from '@mui/icons-material'
import { AvailabilityCheckSearchForm } from 'types/interfaces'
import FormField from 'components/FormField'
import { 
  DataGridPro, 
  GridColDef, 
  GridRenderCellParams, 
  GridValueGetterParams,
  GridEventListener,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid-pro'
import { Observable } from 'rxjs'
import AvailabilityCheck from 'logic/AvailabilityCheck'

interface AdminAvailabilityCheckTableProps {
  reload$?: Observable<boolean>
  actions?: React.ReactNode
  onRowClick?: GridEventListener<"rowClick">
  form: AvailabilityCheckSearchForm
  updateForm: (name: string, value: any) => void
  onDelete?: (check: AvailabilityCheck) => void
}

const AdminAvailabilityCheckTable: React.FC<AdminAvailabilityCheckTableProps> = ({
  reload$,
  actions,
  onRowClick,
  form,
  updateForm,
  onDelete,
}) => {
  const { api } = useAPI()
  const [availabilityChecks, setAvailabilityChecks] = React.useState<AvailabilityCheck[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)

  const fetchAvailabilityChecks = React.useCallback(async () => {
    setLoading(true)
    setAvailabilityChecks(await api.listAvailabilityChecks(form))
    setLoading(false)
  }, [api, form])

  React.useEffect(() => {
    const sub = reload$?.subscribe(() => fetchAvailabilityChecks())
    return () => sub?.unsubscribe()
  }, [fetchAvailabilityChecks, reload$])

  React.useEffect(() => {
    fetchAvailabilityChecks()
  // fetch once on initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns: GridColDef<AvailabilityCheck>[] = React.useMemo(() => [{
    minWidth: 50,
    headerName: 'ID',
    field: 'id',
    type: 'number',
  }, {
    minWidth: 150,
    headerName: 'Status',
    field: 'is_expired',
    type: 'boolean',
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      return (
        <Chip
          size='small'
          label={check.is_expired ? 'Expired' : 'Active'}
          color={check.is_expired ? 'error' : 'success'}
        />
      )
    }
  }, {
    minWidth: 100,
    headerName: 'Available',
    field: 'num_available',
    type: 'number',
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      return (
        <Chip
          size='small'
          label={check.num_available}
          color='success'
        />
      )
    }
  }, {
    minWidth: 100,
    headerName: 'Unavailable',
    field: 'num_unavailable',
    type: 'number',
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      return (
        <Chip
          size='small'
          label={check.num_unavailable}
          color='error'
        />
      )
    }
  }, {
    minWidth: 100,
    headerName: 'Pending',
    field: 'num_pending',
    type: 'number',
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      return (
        <Chip
          size='small'
          label={check.num_pending}
          color='warning'
        />
      )
    }
  }, {
    minWidth: 200,
    headerName: 'Practice',
    field: 'practice_name',
    type: 'string',
  }, {
    minWidth: 250,
    headerName: 'Job Date',
    field: 'start_at',
    type: 'dateTime',
    valueGetter: (params: GridValueGetterParams<AvailabilityCheck>) => {
      return params.row.start_at?.toJSDate()
    },
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      const startAt = check.start_at.setZone('Europe/London')
      const endAt = check.end_at.setZone('Europe/London')
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography>{startAt.toFormat('dd MMM yyyy')}</Typography>
          <Typography variant="caption">{startAt.toFormat('h:mm a')} - {endAt.toFormat('h:mm a')}</Typography>
        </Box>
      )
    }
  }, {
    minWidth: 100,
    headerName: 'Lunch Break',
    field: 'lunch_break',
    type: 'string',
    valueGetter: (params: GridValueGetterParams<AvailabilityCheck>) => {
      return params.row.lunch_break.toFormat('mm') + ' minutes'
    },
  }, {
    minWidth: 100,
    headerName: 'Role',
    field: 'role',
    type: 'string',
  }, {
    minWidth: 200,
    headerName: 'Requirements',
    field: 'requirements',
    type: 'string',
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      const requirements = []
      if (check.sedation_required) requirements.push('Sedation')
      if (check.radiography_required) requirements.push('Radiography')
      if (check.implant_required) requirements.push('Implant')
      if (check.orthodontics_required) requirements.push('Orthodontics')
      
      if (requirements.length === 0) {
        return <Typography>None</Typography>
      }
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1}}>
          {requirements.map(requirement => (
            <Chip
              key={requirement}
              size='small'
              label={requirement}
              color='primary'
            />
          ))}
        </Box>
      )
    },
  }, {
    minWidth: 200,
    headerName: 'Location',
    field: 'postcode',
    type: 'string',
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography>{check.postcode}</Typography>
          <Typography variant="caption">({check.lat.toFixed(4)}, {check.lng.toFixed(4)})</Typography>
        </Box>
      )
    }
  }, {
    minWidth: 200,
    headerName: 'Created At',
    field: 'created_at',
    type: 'dateTime',
    valueGetter: (params: GridValueGetterParams<AvailabilityCheck>) => {
      return params.row.created_at?.toJSDate()
    },
    renderCell: (params: GridRenderCellParams<AvailabilityCheck>) => {
      const check = params.row
      return <Typography>{check.created_at.setZone('Europe/London').toFormat('ccc, LLL dd, yyyy, h:mm a')}</Typography>
    }
  }, {
    minWidth: 100,
    headerName: 'Actions',
    field: 'actions',
    type: 'actions',
    getActions: (params: GridRowParams<AvailabilityCheck>) => [
      <GridActionsCellItem 
        icon={<Delete />} 
        onClick={() => onDelete?.(params.row)} 
        label="Delete" 
      />,
    ]
  }], [onDelete])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        <FormField
          name='search_term'
          label='Search Term'
          onChange={(e) => updateForm('search_term', e.target.value)}
          value={form.search_term ?? ''}
        />
        <FormField
          select
          sx={{ width: 150 }}
          name='is_expired'
          label='Status'
          onChange={(e) => updateForm('is_expired', e.target.value === '' ? undefined : e.target.value === 'true')}
          value={form.is_expired === undefined ? '' : form.is_expired.toString()}>
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='false'>Active</MenuItem>
          <MenuItem value='true'>Expired</MenuItem>
        </FormField>
        <Button
          variant="contained"
          onClick={fetchAvailabilityChecks}
          disabled={loading}>
          {loading ? 'Please Wait ...' : 'Search'}
        </Button>
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {actions}
        </Box>
      </Box>
      {isEmpty(availabilityChecks) ? (
        <Alert severity='info'>No Records Yet</Alert>
      ) : (
        <React.Fragment>
          <Alert severity='info'>{availabilityChecks.length} records returned.</Alert>
          <DataGridPro 
            rows={availabilityChecks}
            columns={columns}
            getRowId={(check) => check.id}
            disableRowSelectionOnClick
            loading={loading}
            onRowDoubleClick={onRowClick}
            hideFooter
          />
        </React.Fragment>
      )}
    </Box>
  )
}

export default AdminAvailabilityCheckTable